import type {
  ComposerContent,
  CompletedAttachment,
  MessageContentPart,
} from "./types";

/**
 * Convert composed content into an array of multimodal content parts.
 *
 * - Text → `{ type: "text", text }`
 * - Image attachments → `{ type: "image_url", image_url: { url } }`
 * - Other files → `{ type: "document", source: { type: "url", url }, media_type }`
 */
export function formatContentParts(
  content: ComposerContent,
): MessageContentPart[] {
  const parts: MessageContentPart[] = [];

  if (content.text) {
    parts.push({ type: "text", text: content.text });
  }

  for (const attachment of content.attachments) {
    parts.push(formatAttachmentPart(attachment));
  }

  return parts;
}

export function formatAttachmentPart(
  attachment: CompletedAttachment,
): MessageContentPart {
  if (attachment.mimeType.startsWith("image/")) {
    return { type: "image_url", image_url: { url: attachment.url } };
  }

  return {
    type: "document",
    source: { type: "url", url: attachment.url },
  };
}

/**
 * Build additional_kwargs for the human message.
 *
 * Two sidecar surfaces ride on additional_kwargs (the API rejects extra
 * fields on content blocks, so per-attachment data has to live here):
 *
 *   1. `metadata.attachments[url] = { filename, mime_type, ...meta }`
 *      — canonical, agent-facing. Surfaced to the agent as a context
 *      message by the `messageMetadataReminder` (langgraph). Covers all
 *      attachments: images get `upload_id` (lets the agent link to its
 *      `uploads` row without a search); docs get filename / mime_type.
 *
 *   2. `document_filenames` / `document_mime_types`
 *      — display-layer only. Doc chips in the chat history reload these
 *      to show friendly names. Images don't need this (they render from
 *      their URL). Migrate readers to (1) and these go away.
 */
export function buildAdditionalKwargs(
  attachments: CompletedAttachment[],
  metadata?: Record<string, unknown>,
): Record<string, unknown> | undefined {
  if (attachments.length === 0 && !metadata) return undefined;

  const attachmentsMetadata: Record<string, Record<string, unknown>> = {};
  const docFilenames: Record<string, string> = {};
  const docMimeTypes: Record<string, string> = {};

  for (const a of attachments) {
    const entry: Record<string, unknown> = {};
    if (a.filename) entry.filename = a.filename;
    if (a.mimeType) entry.mime_type = a.mimeType;
    if (a.meta) Object.assign(entry, a.meta);
    if (Object.keys(entry).length > 0) {
      attachmentsMetadata[a.url] = entry;
    }

    if (!a.mimeType.startsWith("image/")) {
      if (a.filename) docFilenames[a.url] = a.filename;
      docMimeTypes[a.url] = a.mimeType;
    }
  }

  const kwargs: Record<string, unknown> = {};
  const metadataEntries = {
    ...metadata,
    ...(Object.keys(attachmentsMetadata).length > 0 && {
      attachments: attachmentsMetadata,
    }),
  };
  if (Object.keys(metadataEntries).length > 0) {
    kwargs.metadata = metadataEntries;
  }
  if (Object.keys(docFilenames).length > 0) {
    kwargs.document_filenames = docFilenames;
  }
  if (Object.keys(docMimeTypes).length > 0) {
    kwargs.document_mime_types = docMimeTypes;
  }
  return Object.keys(kwargs).length > 0 ? kwargs : undefined;
}

/**
 * Default formatSubmit: wraps content parts in a standard LangGraph message.
 *
 * - Text-only → `{ messages: [{ role: "human", content: "text" }] }`
 * - Multimodal → `{ messages: [{ role: "human", content: [parts...] }] }`
 *
 * Document filenames are stored in `additional_kwargs.document_filenames`
 * so they persist in LangGraph state without being sent to the LLM API.
 */
export function defaultFormatSubmit(
  content: ComposerContent,
): Record<string, unknown> {
  const parts = formatContentParts(content);

  // Text-only: send as plain string for simplicity
  const firstPart = parts[0];
  const messageContent =
    parts.length === 1 && firstPart?.type === "text"
      ? firstPart.text
      : parts;

  const additionalKwargs = buildAdditionalKwargs(
    content.attachments,
    content.metadata,
  );

  return {
    messages: [{
      role: "human",
      content: messageContent,
      ...(additionalKwargs && { additional_kwargs: additionalKwargs }),
    }],
    // Every user message clears any stale `state.error` from a prior
    // failed turn. The langgraph harness boundary writes
    // `{message, node, kind}` into `state.error` when an agent throws;
    // without this clear, the ErrorBanner would stay visible across
    // a successful retry. `null` is the wire-format "clear" sentinel
    // — the graph annotation reducer maps it to `undefined`. State
    // shapes that don't carry an `error` field ignore this key.
    error: null,
  };
}
